import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { BILLING_PRODUCT_PATH, BILLING_PRODUCT_TABS, COSTS_SHEET, COSTS_TAB, PRODUCT_SHEET, type BillingProductTab } from './billing-product.search';

// Manifesto do hub de Produtos de Cobrança (U23/U42). Leve: o fio entra por import dinâmico no `run`.
// As ações com efeito têm `preview` (U24); os textos do diálogo são os do legado com o verbo (U20-6).

const api = () => import('./billing-product.api');
const ref = z.object({ id: z.string().min(1), name: z.string().optional() });
type Ref = z.infer<typeof ref>;
const FIRST_TAB = BILLING_PRODUCT_TABS[0].id;

export const billingProductRefresh = action({
  id: 'billingProduct.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista da aba aberta (produtos de cobrança ou custos de cobrança), sem mudar nada.`,
  run: () => undefined,
});

export const billingProductOpenTab = action<{ tab: BillingProductTab }>({
  id: 'billingProduct.openTab',
  kind: 'navigate',
  page: true,
  label: msg`Abrir aba`,
  description: msg`Troca a aba da tela de Produtos de Cobrança: produtos-cobranca (produtos de cobrança) ou parametros-custos (parâmetros e custos de cobrança). Não muda nada.`,
  input: z.object({ tab: z.enum([BILLING_PRODUCT_TABS[0].id, BILLING_PRODUCT_TABS[1].id]) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === FIRST_TAB ? undefined : input.tab, page: undefined, size: undefined, sheet: undefined, editar: undefined }),
});

/** Página e tamanho da lista da aba aberta (0-based no input, 1-based na URL). */
export const billingProductListPage = listPageAction('billingProduct.list.page');

// ---- aba 1: produtos de cobrança ------------------------------------------------------------------

export const billingProductCreate = action({
  id: 'billingProduct.create',
  kind: 'draft',
  page: true,
  label: msg`Adicionar produto de cobrança`,
  description: msg`Abre o painel em branco de produto de cobrança: código, conta tarifa, dias limite de pagamento, tipo de cobrança, código de juros, envio automático ao cartório e destinatários. Nada é gravado até a pessoa continuar.`,
  permission: can('BillingProduct', 'Create'),
  input: z.object({}),
  run: ({ nav }) => nav.search({ tab: undefined, sheet: PRODUCT_SHEET, editar: undefined }),
});

export const billingProductEdit = action<Ref>({
  id: 'billingProduct.edit',
  kind: 'draft',
  page: true,
  row: true,
  label: msg`Editar`,
  description: msg`Abre o painel de edição de um produto de cobrança, pelo id. Produto que já tem cobranças geradas não pode ser alterado. Nada é gravado até a pessoa salvar.`,
  permission: can('BillingProduct', 'Update'),
  input: ref,
  run: ({ nav, input }) => nav.search({ tab: undefined, sheet: PRODUCT_SHEET, editar: input.id }),
});

export const billingProductRemove = action<Ref>({
  id: 'billingProduct.remove',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui de vez um produto de cobrança, pelo id. Não vale para produto que já tem cobranças geradas. Não dá para desfazer. Pede a confirmação da pessoa.`,
  permission: can('BillingProduct', 'Delete'),
  input: ref,
  preview: (input) => {
    const code = input.name ?? '';
    return { title: i18n._(msg`Excluir produto de cobrança ${code}`), detail: i18n._(msg`Tem certeza que deseja excluir esse produto de cobrança?`), tone: 'destructive' };
  },
  run: async ({ input }) => (await api()).deleteBillingProduct(input.id),
});

// ---- aba 2: custos de cobrança --------------------------------------------------------------------

export const billingProductCostsCreate = action({
  id: 'billingProduct.costs.create',
  kind: 'draft',
  page: true,
  label: msg`Adicionar custos de cobrança`,
  description: msg`Abre o painel em branco de custos de cobrança: código, os doze custos do boleto e as tarifas de Pix. Nada é gravado até a pessoa confirmar.`,
  permission: can('BillingProduct', 'Create'),
  input: z.object({}),
  run: ({ nav }) => nav.search({ tab: COSTS_TAB, sheet: COSTS_SHEET, editar: undefined }),
});

export const billingProductCostsEdit = action<Ref>({
  id: 'billingProduct.costs.edit',
  kind: 'draft',
  page: true,
  row: true,
  label: msg`Editar`,
  description: msg`Abre o painel de edição de um modelo de custos de cobrança, pelo id. Modelo que já tem cobranças geradas não pode ser alterado. Nada é gravado até a pessoa confirmar.`,
  permission: can('BillingProduct', 'Update'),
  input: ref,
  run: ({ nav, input }) => nav.search({ tab: COSTS_TAB, sheet: COSTS_SHEET, editar: input.id }),
});

export const billingProductCostsRemove = action<Ref>({
  id: 'billingProduct.costs.remove',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui de vez um modelo de custos de cobrança, pelo id. Não vale para modelo que já tem cobranças geradas. Não dá para desfazer. Pede a confirmação da pessoa.`,
  permission: can('BillingProduct', 'Delete'),
  input: ref,
  preview: (input) => {
    const code = input.name ?? '';
    return { title: i18n._(msg`Excluir custos de cobrança ${code}`), detail: i18n._(msg`Tem certeza que deseja excluir esse custo de cobrança?`), tone: 'destructive' };
  },
  run: async ({ input }) => (await api()).deleteCostsDefinition(input.id),
});

export const billingProductScreen = defineScreen({
  id: 'billingProduct.hub',
  path: BILLING_PRODUCT_PATH,
  title: msg`Produtos de Cobrança`,
  description: msg`Cadastro de produtos de cobrança e de modelos de custos de cobrança, em duas abas (produtos de cobrança; parâmetros e custos de cobrança), cada uma com lista paginada, painel de adicionar e editar e exclusão.`,
  permission: can('BillingProduct', 'Read'),
  place: { icon: 'receipt-text', section: 'register', tabs: BILLING_PRODUCT_TABS },
  actions: [
    billingProductRefresh,
    billingProductOpenTab,
    billingProductListPage,
    billingProductCreate,
    billingProductEdit,
    billingProductRemove,
    billingProductCostsCreate,
    billingProductCostsEdit,
    billingProductCostsRemove,
  ],
});
